import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Table, Button, Form } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { getStudents, getStudent } from '../../../actions/studentActions'

export default function ProgressView() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { students, currentStudent, loading } = useSelector(state => state.students)
    const [studentId, setStudentId] = useState('')

    useEffect(() => {
        dispatch(getStudents())
    }, [dispatch])

    useEffect(() => {
        if (studentId) dispatch(getStudent(studentId))
    }, [dispatch, studentId])

    const student = currentStudent?.student
    const evaluations = currentStudent?.evaluations || []
    const attendanceHistory = currentStudent?.attendanceHistory || []
    const studentTasks = currentStudent?.tasks || []

    const competenceKeys = ['speaking', 'listening', 'reading', 'writing', 'useOfEnglish']
    const competenceStats = competenceKeys.map(key => {
        const values = evaluations
            .map(ev => ev.competences?.[key])
            .filter(v => v !== null && v !== undefined)
        const avg = values.length > 0 ? values.reduce((a, b) => a + Number(b), 0) / values.length : null
        return { key, avg, count: values.length }
    })

    const attendanceStats = (() => {
        const counts = { present: 0, absent: 0, late: 0, justified: 0 }
        attendanceHistory.forEach(a => { if (counts[a.status] !== undefined) counts[a.status] += 1 })
        const total = attendanceHistory.length
        const attended = counts.present + counts.late + counts.justified
        return { ...counts, total, pct: total > 0 ? Math.round((attended / total) * 100) : null }
    })()

    const tasksStats = (() => {
        const graded = studentTasks.filter(task => task.grade !== null && task.grade !== undefined)
        const avg = graded.length > 0 ? graded.reduce((a, b) => a + Number(b.grade), 0) / graded.length : null
        return { total: studentTasks.length, graded: graded.length, avg }
    })()

    const qualitative = evaluations.filter(ev => ev.comments).slice(0, 5)

    return (
        <Container id='progress-report' className='my-3 p-5'>
            <Row className='align-items-center'>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.evaluations.progressReport')}</h3>
                    <p className='text-muted mb-0'>{t('teacher.evaluations.evolutionsubtitle')}</p>
                </Col>
                <Col xs='auto' className='d-flex gap-2'>
                    <Form.Select
                        style={{ borderRadius: '10px', minWidth: '220px' }}
                        value={studentId}
                        onChange={e => setStudentId(e.target.value)}
                    >
                        <option value=''>{t('teacher.evaluations.selectStudentReport')}</option>
                        {(students || []).map(s => (
                            <option key={s.id} value={s.id}>{s.name}{s.groupName ? ` · ${s.groupName}` : ''}</option>
                        ))}
                    </Form.Select>
                    {student && (
                        <Button variant='outline-secondary' className='rounded-3' onClick={() => window.print()}>
                            <i className='fa-solid fa-print me-1'></i>{t('teacher.evaluations.print')}
                        </Button>
                    )}
                </Col>
            </Row>

            {!studentId
                ? <p className='text-muted mt-4'>{t('teacher.evaluations.selectStudentReport')}</p>
                : loading && !student
                    ? <p className='text-muted mt-4'>{t('global.loading')}</p>
                    : student && (
                        <>
                            <Row className='mt-4'>
                                <Col>
                                    <Card className='border-0 shadow-sm rounded-4'>
                                        <Card.Body className='p-4'>
                                            <h4 className='fw-bold mb-0'>{student.name}</h4>
                                            <p className='text-muted mb-0' style={{ fontSize: '14px' }}>
                                                {(student.enrollments || []).map(e => e.groupName).filter(Boolean).join(' · ')}
                                            </p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Row className='g-3 mt-1'>
                                <Col xs={12} md={4}>
                                    <Card className='border-0 shadow-sm rounded-4 h-100'>
                                        <Card.Body className='p-4 text-center'>
                                            <p className='text-muted mb-1' style={{ fontSize: '13px' }}>{t('teacher.students.attendance')}</p>
                                            <h2 className='fw-bold text-primary mb-0'>{attendanceStats.pct !== null ? `${attendanceStats.pct}%` : '--'}</h2>
                                            <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
                                                {attendanceStats.present} {t('teacher.classes.present').toLowerCase()} · {attendanceStats.total} {t('teacher.classes.session').toLowerCase()}
                                            </p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Card className='border-0 shadow-sm rounded-4 h-100'>
                                        <Card.Body className='p-4 text-center'>
                                            <p className='text-muted mb-1' style={{ fontSize: '13px' }}>{t('teacher.evaluations.title')}</p>
                                            <h2 className='fw-bold text-primary mb-0'>{evaluations.length}</h2>
                                            <p className='text-muted mb-0' style={{ fontSize: '12px' }}>{t('teacher.evaluations.evaluationcount')}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Card className='border-0 shadow-sm rounded-4 h-100'>
                                        <Card.Body className='p-4 text-center'>
                                            <p className='text-muted mb-1' style={{ fontSize: '13px' }}>{t('teacher.students.tasks')}</p>
                                            <h2 className='fw-bold text-primary mb-0'>{tasksStats.avg !== null ? tasksStats.avg.toFixed(1) : '--'}</h2>
                                            <p className='text-muted mb-0' style={{ fontSize: '12px' }}>{tasksStats.graded}/{tasksStats.total} {t('teacher.tasks.graded').toLowerCase()}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Row className='mt-3'>
                                <Col>
                                    <Card className='border-0 shadow-sm rounded-4'>
                                        <Card.Body className='p-4'>
                                            <h5 className='fw-bold mb-3'>{t('teacher.students.progress')}</h5>
                                            <Row className='g-2'>
                                                {competenceStats.map(c => (
                                                    <Col key={c.key} xs={12} md={6} xl={4}>
                                                        <div className='p-3 bg-light rounded-3 h-100'>
                                                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                                                <span className='fw-semibold' style={{ fontSize: '14px' }}>{t(`teacher.students.${c.key === 'useOfEnglish' ? 'useofenglish' : c.key}`)}</span>
                                                                <span className='fw-bold' style={{ fontSize: '14px' }}>{c.avg !== null ? c.avg.toFixed(1) : '--'}</span>
                                                            </div>
                                                            <div className='rounded-pill' style={{ height: '8px', backgroundColor: '#e5e7eb' }}>
                                                                <div className='rounded-pill bg-primary' style={{ height: '8px', width: `${c.avg !== null ? Math.min(100, (c.avg / 10) * 100) : 0}%` }} />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Row className='mt-3'>
                                <Col>
                                    <Card className='border-0 shadow-sm rounded-4'>
                                        <Card.Body className='p-4'>
                                            <h5 className='fw-bold mb-3'>{t('teacher.evaluations.evolution')}</h5>
                                            {evaluations.length > 0
                                                ? <Table hover responsive className='mb-0'>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('global.date')}</th>
                                                            <th>{t('teacher.students.evaltype')}</th>
                                                            <th>{t('teacher.students.evalscore')}</th>
                                                            <th>{t('teacher.students.speaking')[0]}.</th>
                                                            <th>{t('teacher.students.listening')[0]}.</th>
                                                            <th>{t('teacher.students.reading')[0]}.</th>
                                                            <th>{t('teacher.students.writing')[0]}.</th>
                                                            <th>UoE</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {evaluations.map(ev => (
                                                            <tr key={ev._id}>
                                                                <td>{ev.date ? new Date(ev.date).toLocaleDateString() : '-'}</td>
                                                                <td><span className='badge bg-light text-dark border'>{ev.type}</span></td>
                                                                <td className='fw-semibold'>{ev.score}/{ev.maxScore}</td>
                                                                <td>{ev.competences?.speaking ?? '-'}</td>
                                                                <td>{ev.competences?.listening ?? '-'}</td>
                                                                <td>{ev.competences?.reading ?? '-'}</td>
                                                                <td>{ev.competences?.writing ?? '-'}</td>
                                                                <td>{ev.competences?.useOfEnglish ?? '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                : <p className='text-muted mb-0'>{t('teacher.students.noevaluations')}</p>
                                            }
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {qualitative.length > 0 && (
                                <Row className='mt-3'>
                                    <Col>
                                        <Card className='border-0 shadow-sm rounded-4'>
                                            <Card.Body className='p-4'>
                                                <h5 className='fw-bold mb-3'>{t('teacher.evaluations.comments')}</h5>
                                                {qualitative.map(ev => (
                                                    <div key={ev._id} className='p-3 mb-2 bg-light rounded-3'>
                                                        <p className='mb-1' style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>{ev.comments}</p>
                                                        <p className='text-muted mb-0' style={{ fontSize: '11px' }}>
                                                            {ev.date ? new Date(ev.date).toLocaleDateString() : ''}{ev.period ? ` · ${ev.period}` : ''}
                                                        </p>
                                                    </div>
                                                ))}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )
            }
        </Container>
    )
}
